import { mdiChevronRight } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { CellProps } from 'react-table';
import {
    getPayerLanes,
    IRoutableConnection,
    IRoutablePayer,
    PayerLaneId,
} from '../../services/payer-routing';
import { ITableColumn } from '../data-table/data-table.presenter';
import { CountBadge, PathCell, UNKNOWN_PATH } from '../payer-path/payer-path';
import * as S from './payer-columns.styles';

/**
 * The payers list, as columns — one definition rendered by both apps.
 *
 * It was two: the support console's `payer-*-column` modules and a parallel set built inline in
 * the provider page. They drifted, as two implementations of one thing do, and every visual
 * defect reported against this screen turned out to exist on exactly one side (GP-21).
 *
 * The support version is the reference. What differs between the apps is passed in rather than
 * forked: where the clearinghouse join comes from, which tags count as EMR payers, what the
 * name column is called, and whether a Halt column exists at all.
 *
 * Nothing here knows `SupportPayer` from `ProviderPayer`. They are structurally identical for
 * routing purposes and differ only in name, which is the whole reason there were ever two of
 * these (GP-20).
 */

/** The payer fields these columns read. Both apps' models satisfy it. */
export interface IColumnPayer extends IRoutablePayer {
    id?: string;
    coverage?: string;
    halt?: boolean;
}

export interface IPayerColumnsOptions<T extends IColumnPayer> {
    /** The clearinghouse join, from whichever store the host owns. */
    getBindings: (payerId: string | undefined) => IRoutableConnection[] | undefined;
    /**
     * The EMR payer identifiers to count. Support filters to `INTEGRATION` because its payers
     * also carry USER, SYSTEM, ELIGIBILITY and ERA tags; the provider tag type has exactly one
     * member, so every tag qualifies there.
     */
    getEmrTags: (payer: T) => Array<{ id?: string; name?: string }>;
    /**
     * True when the payer has tags but none of them are EMR payers — support cannot yet assert
     * "nothing arrives from the client's system", so it renders unknown rather than zero
     * (NS-3). The provider app has no such case and omits this.
     */
    isEmrCountUnknown?: (payer: T) => boolean;
    /** Support calls it "ENTER payer" to separate it from an EMR payer; provider says "Payer". */
    identityHeader?: string;
    /** Sort key for the name column, if the host's endpoint can sort by it. */
    identitySortType?: ITableColumn<T>['sortType'];
    /** Support shows halt as its own column as well as a badge; provider shows only the badge. */
    includeHalt?: boolean;
}

function humanise(coverage: string | undefined): string {
    return coverage
        ? String(coverage)
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/^\w/, c => c.toUpperCase())
        : '—';
}

/** The three lanes the list summarises. The expanded row shows all nine. */
const LANE_COLUMNS: Array<{ lane: PayerLaneId; id: string; header: string }> = [
    { lane: 'claim', id: 'ClaimData', header: 'Claim data' },
    { lane: 'reconsideration', id: 'Reconsiderations', header: 'Reconsiderations' },
    { lane: 'paperwork', id: 'Paperwork', header: 'Paperwork' },
];

export function buildPayerColumns<T extends IColumnPayer>(
    options: IPayerColumnsOptions<T>,
): ITableColumn<T>[] {
    const { getBindings, getEmrTags, isEmrCountUnknown, identityHeader = 'Payer', identitySortType } = options;

    /**
     * `Header` is an empty STRING, not an element. An element in a column header crashes the
     * app outright with "Maximum call stack size exceeded" — the presenter cannot take one.
     */
    const expander: ITableColumn<T> = {
        id: 'Expander',
        Header: '',
        accessor: (): string => '',
        Cell: ({ row }: CellProps<T>): React.ReactElement => (
            // `isExpanded` is contributed by react-table's useExpanded plugin and is absent
            // from the base Row type in this package's typings.
            <S.Chevron expanded={!!(row as { isExpanded?: boolean }).isExpanded} aria-hidden={true}>
                <Icon path={mdiChevronRight} size={0.8} />
            </S.Chevron>
        ),
        disableSortBy: true,
        maxWidth: 40,
    };

    const IdentityCell = observer(({ row }: CellProps<T>): React.ReactElement => {
        const payer = row.original;
        const bindings = getBindings(payer.id);

        // Three states, not two. `undefined` is "the join has not resolved" and says nothing
        // about routing, so it contributes no text rather than a reassuring or an alarming
        // one. Only a resolved empty list may say "no clearinghouse connection" (NS-3).
        const connection =
            bindings === undefined
                ? undefined
                : bindings.length === 0
                  ? 'No clearinghouse connection'
                  : `Clearinghouse · ${bindings.length} connection${bindings.length === 1 ? '' : 's'}`;

        const subtitle = [humanise(payer.coverage), connection].filter(Boolean).join(' · ');

        return (
            <S.Identity>
                <S.NameRow>
                    <S.Name title={payer.name}>{payer.name}</S.Name>
                    {payer.halt && <S.Halted>HALTED</S.Halted>}
                </S.NameRow>
                <S.Sub title={subtitle}>{subtitle}</S.Sub>
            </S.Identity>
        );
    });

    const EmrCell = observer(({ row }: CellProps<T>): React.ReactElement | string => {
        if (isEmrCountUnknown?.(row.original)) {
            return UNKNOWN_PATH;
        }

        return <CountBadge count={getEmrTags(row.original).length} />;
    });

    /**
     * A `ClearinghousePayer` is its own entity with its own EDI number, not three attributes of
     * the payer (NS-5) — hence one column showing the link. A payer may have several; the
     * design shows the first and counts the rest, because naming one primary would assert an
     * ordering the API does not expose (FE-21).
     */
    const ClearinghouseCell = observer(({ row }: CellProps<T>): React.ReactElement | string => {
        const bindings = getBindings(row.original.id);

        if (!bindings) {
            return UNKNOWN_PATH;
        }

        if (bindings.length === 0) {
            return (
                <S.Cell>
                    <S.Name>None — paper only</S.Name>
                    <S.Meta>no clearinghouse connection</S.Meta>
                </S.Cell>
            );
        }

        const [first, ...rest] = bindings;
        const transactions = first.transactions || [];

        return (
            <S.Cell>
                <S.NameRow>
                    <S.Name title={first.payer?.name || first.clearinghouse?.name}>
                        {first.payer?.name || first.clearinghouse?.name || 'Clearinghouse payer'}
                    </S.Name>
                    {rest.length > 0 && <S.More>{`+${rest.length} more`}</S.More>}
                </S.NameRow>
                <S.Meta>
                    {`${first.clearinghouse?.name || 'Clearinghouse'} · EDI ${first.externalId || '—'} · ${
                        transactions.length
                    } tx`}
                </S.Meta>
            </S.Cell>
        );
    });

    function laneColumn(laneId: PayerLaneId, id: string, header: string): ITableColumn<T> {
        const Cell = observer(({ row }: CellProps<T>): React.ReactElement | string => {
            const payer = row.original;
            const bindings = getBindings(payer.id);

            // Withhold rather than guess. "Not routed" is a claim an operator acts on, and
            // until the join resolves we do not know it (NS-3).
            if (!bindings && laneId === 'claim') {
                return UNKNOWN_PATH;
            }

            const lane = getPayerLanes(payer, bindings).find(l => l.id === laneId)!;

            return <PathCell path={lane.path} destination={lane.destination} />;
        });

        return { id, Header: header, accessor: (): string => '', Cell, disableSortBy: true, minWidth: 145 };
    }

    const columns: ITableColumn<T>[] = [
        expander,
        {
            id: 'EnterPayer',
            Header: identityHeader,
            accessor: 'name',
            Cell: IdentityCell,
            disableSortBy: !identitySortType,
            sortType: identitySortType,
            minWidth: 250,
        },
        { id: 'EmrPayers', Header: 'EMR payers', accessor: (): string => '', Cell: EmrCell, disableSortBy: true, maxWidth: 110 },
        {
            id: 'ClearinghousePayer',
            Header: 'Clearinghouse payer',
            accessor: (): string => '',
            Cell: ClearinghouseCell,
            disableSortBy: true,
            minWidth: 220,
        },
        ...LANE_COLUMNS.map(c => laneColumn(c.lane, c.id, c.header)),
    ];

    if (options.includeHalt) {
        columns.push({
            id: 'Halt',
            Header: 'Halt',
            accessor: (): string => '',
            Cell: observer(({ row }: CellProps<T>): React.ReactElement | null =>
                row.original.halt ? <S.Halted>HALTED</S.Halted> : null,
            ),
            // `SupportPayerSortType` has no HALT member, so the server cannot order by it and a
            // sortable header would be a control that does nothing.
            disableSortBy: true,
            maxWidth: 110,
        });
    }

    return columns;
}
